import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { RootLayout } from './components/Layout/RootLayout'
import { DEMO_SHOWCASE_PATH, SHOWCASE_PATH } from './features/auto-test/showcase/routes'
import { DEMO_EXPO_PATH, EXPO_PATH } from './features/auto-test/expo/routes'
import { DeferredReviewProvider } from './features/trace-chain/DeferredReviewProvider'
import { clearCatalogUnlock } from './features/manufacturing-catalog/catalogLock'

// Feature pages (lazy loaded)
import { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Center,
  chakra,
  Flex,
  Image,
  Input,
  Link,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'

const ContractReferencePage = lazy(() => import('./features/getting-started/ContractReferencePage').then(m => ({ default: m.ContractReferencePage })))
const TestIntentGuidePage = lazy(() => import('./features/getting-started/TestIntentGuidePage'))
const CrossDomainConceptPage = lazy(() => import('./features/getting-started/CrossDomainConceptPage'))
const DslRoadmapPage = lazy(() => import('./features/getting-started/DslRoadmapPage'))
const FaqPage = lazy(() => import('./features/faq/FaqPage').then(m => ({ default: m.FaqPage })))
const ApiDocsPage = lazy(() => import('./features/api-docs/ApiDocsPage').then(m => ({ default: m.ApiDocsPage })))
const AutoTestPage = lazy(() => import('./features/auto-test/AutoTestPage'))
const ShowcasePage = lazy(() => import('./features/auto-test/showcase/ShowcasePage'))
const ExpoPage = lazy(() => import('./features/auto-test/expo/ExpoPage'))
const FtaAutomationPage = lazy(() => import('./features/fta-automation/FtaAutomationPage'))
const FtaTreePage = lazy(() => import('./features/fta-automation/FtaTreePage'))
const FmeaToFtaPage = lazy(() => import('./features/fta-automation/FmeaToFtaPage'))
const GeneratedFtaGalleryPage = lazy(() => import('./features/fta-automation/GeneratedFtaGalleryPage'))
const BlockDiagramFtaPage = lazy(() => import('./features/fta-automation/BlockDiagramFtaPage'))
const ReqGuardPage = lazy(() => import('./features/req-guard/ReqGuardPage'))
const AutoKbPage = lazy(() => import('./features/auto-kb/AutoKbPage'))
const ConvertToMarkdownPage = lazy(() => import('./features/auto-kb/ConvertToMarkdownPage'))
const CamberVisualizerPage = lazy(() => import('./features/camber-visualizer/CamberVisualizerPage'))
const MotoSuspensionPage = lazy(() => import('./features/moto-suspension/MotoSuspensionPage'))
const DesignerWorkshopPage = lazy(() => import('./features/moto-suspension/DesignerWorkshopPage'))
const ABTestPage = lazy(() => import('./features/ab-test/ABTestPage'))
const FtaOverviewPage = lazy(() => import('./features/getting-started/FtaOverviewPage').then(m => ({ default: m.FtaOverviewPage })))
const FtaTourPage = lazy(() => import('./features/getting-started/FtaTourPage'))
const AdasPage = lazy(() => import('./features/adas/AdasPage'))
const AutoValidationPage = lazy(() => import('./features/auto-validation/AutoValidationPage'))
const SettingsPage = lazy(() => import('./features/settings/SettingsPage'))
const UserTrafficPage = lazy(() => import('./features/user-traffic/UserTrafficPage'))
const TraceCompletenessPage = lazy(() => import('./features/trace-completeness/TraceCompletenessPage'))
const ChainPathDetailPage = lazy(() => import('./features/trace-chain/ChainPathDetailPage'))
const CpOnlyRowDetailPage = lazy(() => import('./features/trace-chain/CpOnlyRowDetailPage'))
const ControlPlanImportPage = lazy(() => import('./features/control-plan-review/ControlPlanImportPage'))
const ControlPlanReviewPage = lazy(() => import('./features/control-plan-review/ControlPlanReviewPage'))
const ControlPlanPdfImportPage = lazy(() => import('./features/control-plan-review/v2/ControlPlanPdfImportPage'))
const ControlPlanPdfReviewPage = lazy(() => import('./features/control-plan-review/v2/ControlPlanPdfReviewPage'))
const ControlPlanVisionPocPage = lazy(() => import('./features/control-plan-vision-poc/ControlPlanVisionPocPage'))
const CpDiffAnalysisPage = lazy(() => import('./features/cp-diff-analysis/CpDiffAnalysisPage'))
const CpChangeWorkbenchPage = lazy(() => import('./features/cp-change-workbench/CpChangeWorkbenchPage'))
const CatalogImportPage = lazy(() => import('./features/catalog-import/CatalogImportPage'))
const ManufacturingCatalogPage = lazy(() => import('./features/manufacturing-catalog/ManufacturingCatalogPage'))
const RelationshipGraphReviewPage = lazy(() => import('./features/manufacturing-catalog/RelationshipGraphReviewPage'))
const DocumentDetailPage = lazy(() => import('./features/manufacturing-catalog/DocumentDetailPage'))
const OperationDetailPage = lazy(() => import('./features/manufacturing-catalog/OperationDetailPage'))
const CrossCheckDetailPage = lazy(() => import('./features/manufacturing-catalog/CrossCheckDetailPage'))
const DesignCatalogPage = lazy(() => import('./features/design-catalog/DesignCatalogPage'))
const CatalogLockGate = lazy(() => import('./features/manufacturing-catalog/CatalogLockGate').then(m => ({ default: m.CatalogLockGate })))
const NotFoundPage = lazy(() => import('./components/NotFoundPage'))
const UnderDevelopmentPage = lazy(() => import('./features/under-development/UnderDevelopmentPage').then(m => ({ default: m.UnderDevelopmentPage })))
const DemoEntryPage = lazy(() => import('./features/demo/DemoEntryPage').then(m => ({ default: m.DemoEntryPage })))
const SandboxLayout = lazy(() => import('./features/demo/SandboxLayout').then(m => ({ default: m.SandboxLayout })))
import { useTranslation } from 'react-i18next'
import { setCurrentUser } from './lib/apiFetch'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { ProfileForm } from './components/ProfileForm'
import { VscAzure } from 'react-icons/vsc'
import { LoginButton } from '@/components/auth/LoginButton'
const LoginBackground = lazy(() => import('./components/r3f/LoginBackground'))

const PageLoader = () => (
  <Center h="100vh">
    <Spinner size="xl" color="brand.500" borderWidth="4px" />
  </Center>
)

// State machine instead of independent booleans
type AppView = 'loading' | 'login' | 'access-request' | 'profile' | 'app' | 'demo'

// Build-time shared password (baked in via VITE_LOGIN_PASSWORD). Empty string = disabled.
// NOTE: visible in the JS bundle — UI gate only, not real authentication. Backend
// API calls still require an OAuth session cookie.
const SHARED_PASSWORD = import.meta.env.VITE_LOGIN_PASSWORD || ''
// Stores a non-secret marker (the unlock-version), never the password itself.
const PASSWORD_UNLOCK_KEY = 'cypremos.passwordUnlock'
const PASSWORD_UNLOCK_VALUE = '1'

function App() {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const isOfflineShowcase = location.pathname === DEMO_SHOWCASE_PATH
  const isOfflineExpo = location.pathname === DEMO_EXPO_PATH
  const [view, setView] = useState<AppView>('loading')
  const [passwordInput, setPasswordInput] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [devPasswordInput, setDevPasswordInput] = useState('')
  const [devPasswordError, setDevPasswordError] = useState('')

  const verifyToken = useCallback(async () => {
    const isDemo = window.location.pathname.startsWith('/demo')

    // Password unlock is a UI-only gate — it can show the login screen, but
    // it cannot grant access to the authenticated app shell because every
    // /api/* call requires a real OAuth session cookie. Always route to the
    // login screen (or demo) when /api/auth/verify fails; do not silently
    // promote to 'app'.
    const onVerifyFailed = () => {
      sessionStorage.removeItem(PASSWORD_UNLOCK_KEY)
      // The manufacturing catalog's shared-password unlock lives as long as the
      // login session that was allowed to see it, and no longer.
      clearCatalogUnlock()
      setView(isDemo ? 'demo' : 'login')
    }

    try {
      const res = await fetch('/api/auth/verify', { credentials: 'include' })
      if (res.ok) {
        const data = await res.json() as { valid: boolean; user: { hasProfile?: boolean; sub: string; email?: string; username?: string; name?: string; avatar?: string; role: string } }
        setCurrentUser(data.user)
        if (data.user.role === 'guest') {
          setView('demo')
          return
        }
        // Non-guest user on a /demo/* URL — redirect to the full app
        if (isDemo) {
          window.location.replace('/auto-test')
          return
        }
        const questionnaireEnabled = import.meta.env.VITE_FEATURE_QUESTIONNAIRE === 'on'
        setView(import.meta.env.PROD && questionnaireEnabled && data.user.hasProfile === false ? 'profile' : 'app')
      } else {
        onVerifyFailed()
      }
    } catch {
      onVerifyFailed()
    }
  }, [])

  useEffect(() => {
    if (!isOfflineShowcase && !isOfflineExpo) void verifyToken()
  }, [isOfflineShowcase, isOfflineExpo, verifyToken])

  // These exact public routes contain bundled synthetic data only. They can boot
  // from a local static server without an auth service or an external network.
  if (isOfflineShowcase || isOfflineExpo) {
    return (
      <Suspense fallback={<Box minH="100dvh" bg="#070A12" />}>
        {isOfflineExpo ? <ExpoPage /> : <ShowcasePage />}
      </Suspense>
    )
  }

  if (view === 'loading') {
    return <PageLoader />
  }

  if (view === 'access-request') {
    return (
      <ProfileForm
        mode="request"
        onComplete={() => setView('login')}
        onBack={() => setView('login')}
      />
    )
  }

  if (view === 'login') {
    return (
      <Box position="relative" minH="100vh" w="full" bg="white" overflow="hidden">
        {/* Background Canvas */}
        <Box position="absolute" inset={0} zIndex={0}>
          <Suspense fallback={null}>
            <LoginBackground />
          </Suspense>
        </Box>

        {/* Foreground Content */}
        <Flex
          position="relative"
          zIndex={1}
          direction="column"
          align="center"
          justify="center"
          minH="100vh"
          px={4}
          py={12}
        >
          {/* Top Logo */}
          <Flex align="center" gap={2} mb={{ base: 12, md: 24 }}>
            <Image src="/logo_cypremos.png" alt="Cypremos" maxH="48px" />
          </Flex>

          {/* Main Hero Text */}
          <Text
            fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }}
            fontWeight="medium"
            color="gray.900"
            textAlign="center"
            lineHeight="1.05"
            letterSpacing="-0.03em"
            maxW="5xl"
            mb={16}
          >
            {t('login.hero')}
          </Text>

          {/* Password login (only when build was given a shared password) */}
          {SHARED_PASSWORD && (
            <chakra.form
              mb={8}
              w="full"
              maxW="sm"
              onSubmit={(e) => {
                e.preventDefault()
                if (passwordInput === SHARED_PASSWORD) {
                  sessionStorage.setItem(PASSWORD_UNLOCK_KEY, PASSWORD_UNLOCK_VALUE)
                  setPasswordInput('')
                  setPasswordError(t('login.passwordAccepted'))
                } else {
                  setPasswordError(t('login.passwordIncorrect'))
                }
              }}
            >
              <VStack gap={3}>
                <Input
                  type="password"
                  placeholder={t('login.passwordPlaceholder')}
                  value={passwordInput}
                  onChange={e => { setPasswordInput(e.target.value); setPasswordError('') }}
                  bg="white"
                  borderColor="gray.200"
                  color="gray.900"
                  borderRadius="full"
                  px={6}
                  h="12"
                  autoComplete="off"
                  _focus={{ borderColor: 'gray.400', boxShadow: 'none' }}
                />
                {passwordError && <Text color="red.500" fontSize="sm" textAlign="center">{passwordError}</Text>}
                <Button type="submit" size="lg" bg="black" color="white" _hover={{ bg: 'gray.800' }} borderRadius="full" w="full" h="12">
                  {t('login.signInWithPassword')}
                </Button>
              </VStack>
            </chakra.form>
          )}

          {/* Local dev-only login: password 1234 issues a real backend session
              cookie via POST /api/auth/dev-login (dev-server only, never built
              into deployed bundles). Separate from the VITE_LOGIN_PASSWORD UI
              gate above, which does not create a backend session. */}
          {import.meta.env.DEV && (
            <chakra.form
              mb={8}
              w="full"
              maxW="sm"
              onSubmit={async (e) => {
                e.preventDefault()
                const res = await fetch('/api/auth/dev-login', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ password: devPasswordInput }),
                })
                if (res.ok) {
                  setDevPasswordInput('')
                  await verifyToken()
                } else {
                  setDevPasswordError(t('login.passwordIncorrect'))
                }
              }}
            >
              <VStack gap={3}>
                <Text fontSize="xs" color="gray.500">Local dev login</Text>
                <Input
                  type="password"
                  placeholder="1234"
                  value={devPasswordInput}
                  onChange={e => { setDevPasswordInput(e.target.value); setDevPasswordError('') }}
                  bg="white"
                  borderColor="gray.200"
                  color="gray.900"
                  borderRadius="full"
                  px={6}
                  h="12"
                  autoComplete="off"
                  _focus={{ borderColor: 'gray.400', boxShadow: 'none' }}
                />
                {devPasswordError && <Text color="red.500" fontSize="sm" textAlign="center">{devPasswordError}</Text>}
                <Button type="submit" size="lg" variant="outline" borderRadius="full" w="full" h="12">
                  Dev sign in
                </Button>
              </VStack>
            </chakra.form>
          )}

          <Flex gap={4} direction="column" w="full" maxW="sm">
            <LoginButton
              icon={VscAzure}
              label={t('login.signInAzure')}
              href="/api/auth/azure"
              iconColor="#0078d4"
            />
          </Flex>

          {/* Bottom language toggle and request access */}
          <Flex position="absolute" bottom={8} left={0} right={0} mx="auto" w="full" justify="space-between" px={8} maxW="7xl">
            <Button
              size="sm"
              variant="plain"
              color="gray.500"
              _hover={{ color: 'gray.800' }}
              onClick={() => { const next = i18n.language?.startsWith('ja') ? 'en' : 'ja'; i18n.changeLanguage(next) }}
            >
              {i18n.language?.startsWith('ja') ? 'English' : '日本語'}
            </Button>
            {import.meta.env.PROD ? (
              <Button
                variant="plain"
                size="sm"
                color="gray.500"
                _hover={{ color: 'gray.800' }}
                onClick={() => setView('access-request')}
              >
                {t('login.requestAccess')}
              </Button>
            ) : null}
          </Flex>

          {/* Deprecation warning */}
          {import.meta.env.VITE_APP_ENV !== 'sdv' && (
            <Box position="absolute" top={4} w="full" px={4} maxW="2xl">
              <Alert.Root status="warning" variant="subtle" borderRadius="md" fontSize="sm" bg="orange.50" borderWidth="1px" borderColor="orange.200" color="orange.800">
                <Alert.Indicator />
                <Alert.Description>
                  {t('login.deprecationWarning')}{' '}
                  <Link
                    href="https://sdv.cypremos.com"
                    target="_blank" rel="noopener noreferrer"
                    fontWeight="semibold"
                    textDecoration="underline"
                  >
                    sdv.cypremos.com
                  </Link>
                </Alert.Description>
              </Alert.Root>
            </Box>
          )}
        </Flex>
      </Box>
    )
  }

  if (view === 'profile') {
    return (
      <ProfileForm
        mode="profile"
        onComplete={() => setView('app')}
        onSkip={() => setView('app')}
      />
    )
  }

  if (location.pathname === '/fta-tree') {
    return (
      <Routes>
        <Route
          path="/fta-tree"
          element={
            <Suspense fallback={<PageLoader />}>
              <FtaTreePage />
            </Suspense>
          }
        />
      </Routes>
    )
  }

  if (view === 'demo') {
    return (
      <Routes>
        <Route
          path="/demo"
          element={
            <Suspense fallback={<PageLoader />}>
              <DemoEntryPage />
            </Suspense>
          }
        />
        <Route
          path="/demo/*"
          element={
            <Suspense fallback={<PageLoader />}>
              <SandboxLayout />
            </Suspense>
          }
        >
          <Route path="auto-test" element={<Suspense fallback={<PageLoader />}><AutoTestPage /></Suspense>} />
          <Route path="moto-suspension" element={<Suspense fallback={<PageLoader />}><MotoSuspensionPage /></Suspense>} />
          <Route path="camber-visualizer" element={<Suspense fallback={<PageLoader />}><CamberVisualizerPage /></Suspense>} />
          {/* req-guard is authorized-only (demo/guest removed, §0a); see the authorized route below. */}
          <Route path="*" element={<Navigate to="/demo/auto-test" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/demo" replace />} />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route
        path={SHOWCASE_PATH}
        element={<Suspense fallback={<Box minH="100dvh" bg="#070A12" />}><ShowcasePage /></Suspense>}
      />
      <Route
        path={EXPO_PATH}
        element={<Suspense fallback={<Box minH="100dvh" bg="#070A12" />}><ExpoPage /></Suspense>}
      />
      <Route path="/" element={<DeferredReviewProvider><RootLayout /></DeferredReviewProvider>}>
        {/* Public routes — accessible to all authenticated users */}
        <Route index element={<Navigate to="/auto-test" replace />} />
        <Route
          path="auto-test"
          element={
            <Suspense fallback={<PageLoader />}>
              <AutoTestPage />
            </Suspense>
          }
        />

        {/* Protected routes — only authorized email domains can access these */}
        <Route element={<ProtectedRoute />}>
          <Route
            path="getting-started"
            element={
              <Suspense fallback={<PageLoader />}>
                <ContractReferencePage />
              </Suspense>
            }
          />
          <Route
            path="getting-started/dsl-guide"
            element={
              <Suspense fallback={<PageLoader />}>
                <TestIntentGuidePage />
              </Suspense>
            }
          />
          <Route
            path="getting-started/cross-domain"
            element={
              <Suspense fallback={<PageLoader />}>
                <CrossDomainConceptPage />
              </Suspense>
            }
          />
          <Route
            path="getting-started/dsl-roadmap"
            element={
              <Suspense fallback={<PageLoader />}>
                <DslRoadmapPage />
              </Suspense>
            }
          />
          <Route
            path="getting-started/fta-tour"
            element={
              <Suspense fallback={<PageLoader />}>
                <FtaTourPage />
              </Suspense>
            }
          />
          <Route
            path="fta-overview"
            element={
              <Suspense fallback={<PageLoader />}>
                <FtaOverviewPage />
              </Suspense>
            }
          />
          <Route
            path="faq"
            element={
              <Suspense fallback={<PageLoader />}>
                <FaqPage />
              </Suspense>
            }
          />
          <Route
            path="api-docs"
            element={
              <Suspense fallback={<PageLoader />}>
                <ApiDocsPage />
              </Suspense>
            }
          />
          <Route
            path="fta-automation"
            element={
              <Suspense fallback={<PageLoader />}>
                <FtaAutomationPage />
              </Suspense>
            }
          />
          <Route
            path="fmea-upload"
            element={
              <Suspense fallback={<PageLoader />}>
                <FmeaToFtaPage />
              </Suspense>
            }
          />
          <Route
            path="generated-fta"
            element={
              <Suspense fallback={<PageLoader />}>
                <GeneratedFtaGalleryPage />
              </Suspense>
            }
          />
          <Route
            path="block-diagram-fta"
            element={<Suspense fallback={<PageLoader />}><BlockDiagramFtaPage /></Suspense>}
          />
          <Route
            path="req-guard"
            element={
              <Suspense fallback={<PageLoader />}>
                <ReqGuardPage />
              </Suspense>
            }
          />
          <Route
            path="auto-kb"
            element={
              <Suspense fallback={<PageLoader />}>
                <AutoKbPage />
              </Suspense>
            }
          />
          <Route
            path="auto-kb/convert"
            element={
              <Suspense fallback={<PageLoader />}>
                <ConvertToMarkdownPage />
              </Suspense>
            }
          />
          <Route
            path="camber-visualizer"
            element={
              <Suspense fallback={<PageLoader />}>
                <CamberVisualizerPage />
              </Suspense>
            }
          />
          <Route
            path="moto-suspension"
            element={
              <Suspense fallback={<PageLoader />}>
                <MotoSuspensionPage />
              </Suspense>
            }
          />
          <Route
            path="moto-suspension/designer"
            element={
              <Suspense fallback={<PageLoader />}>
                <DesignerWorkshopPage />
              </Suspense>
            }
          />
          <Route
            path="ab-test"
            element={
              <Suspense fallback={<PageLoader />}>
                <ABTestPage />
              </Suspense>
            }
          />
          <Route
            path="adas"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdasPage />
              </Suspense>
            }
          />
          <Route
            path="auto-validation"
            element={
              <Suspense fallback={<PageLoader />}>
                <AutoValidationPage />
              </Suspense>
            }
          />
          <Route
            path="settings"
            element={
              <Suspense fallback={<PageLoader />}>
                <SettingsPage />
              </Suspense>
            }
          />
          <Route
            path="user-traffic"
            element={
              <Suspense fallback={<PageLoader />}>
                <UserTrafficPage />
              </Suspense>
            }
          />

          <Route
            path="poc/trace-completeness"
            element={
              <Suspense fallback={<PageLoader />}>
                <TraceCompletenessPage />
              </Suspense>
            }
          />

          <Route
            path="trace-chain/:buildId/:subjectId/:pathIndex"
            element={
              <Suspense fallback={<PageLoader />}>
                <ChainPathDetailPage />
              </Suspense>
            }
          />

          <Route
            path="trace-chain/:buildId/cp/:cpSubjectId/:stepIndex"
            element={
              <Suspense fallback={<PageLoader />}>
                <CpOnlyRowDetailPage />
              </Suspense>
            }
          />

          <Route
            path="control-plan/import"
            element={
              <Suspense fallback={<PageLoader />}>
                <ControlPlanImportPage />
              </Suspense>
            }
          />
          <Route
            path="control-plan/review/:versionId"
            element={
              <Suspense fallback={<PageLoader />}>
                <ControlPlanReviewPage />
              </Suspense>
            }
          />
          <Route
            path="control-plan/pdf/import"
            element={
              <Suspense fallback={<PageLoader />}>
                <ControlPlanPdfImportPage />
              </Suspense>
            }
          />
          <Route
            path="control-plan/pdf/review/:versionId"
            element={
              <Suspense fallback={<PageLoader />}>
                <ControlPlanPdfReviewPage />
              </Suspense>
            }
          />
          <Route
            path="control-plan-vision-poc"
            element={
              <Suspense fallback={<PageLoader />}>
                <ControlPlanVisionPocPage />
              </Suspense>
            }
          />
          <Route
            path="cp-diff-analysis"
            element={
              <Suspense fallback={<PageLoader />}>
                <CpDiffAnalysisPage />
              </Suspense>
            }
          />
          <Route
            path="cp-change-workbench"
            element={
              <Suspense fallback={<PageLoader />}>
                <CpChangeWorkbenchPage />
              </Suspense>
            }
          />
          <Route
            path="catalog-import"
            element={
              <Suspense fallback={<PageLoader />}>
                <CatalogImportPage />
              </Suspense>
            }
          />
          <Route
            path="design-catalog"
            element={
              <Suspense fallback={<PageLoader />}>
                <DesignCatalogPage />
              </Suspense>
            }
          />
          {/* Vendor documents: one shared-password gate covers the index and every
              deep link below it. UI gate only — see catalogLock.ts. */}
          <Route
            path="manufacturing-catalog"
            element={
              <Suspense fallback={<PageLoader />}>
                <CatalogLockGate />
              </Suspense>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<PageLoader />}>
                  <ManufacturingCatalogPage />
                </Suspense>
              }
            />
            <Route
              path="graph"
              element={
                <Suspense fallback={<PageLoader />}>
                  <RelationshipGraphReviewPage />
                </Suspense>
              }
            />
            <Route
              path="operations/:operationNumber"
              element={
                <Suspense fallback={<PageLoader />}>
                  <OperationDetailPage />
                </Suspense>
              }
            />
            <Route
              path="operations/:operationNumber/cross-check/:cpRow"
              element={
                <Suspense fallback={<PageLoader />}>
                  <CrossCheckDetailPage />
                </Suspense>
              }
            />
            <Route
              path=":kind/:name"
              element={
                <Suspense fallback={<PageLoader />}>
                  <DocumentDetailPage />
                </Suspense>
              }
            />
          </Route>

          {/* SYS and SWE under development routes */}
          <Route
            path=":tool/under-development"
            element={
              <Suspense fallback={<PageLoader />}>
                <UnderDevelopmentPage />
              </Suspense>
            }
          />

          <Route
            path="*"
            element={
              <Suspense fallback={<PageLoader />}>
                <NotFoundPage />
              </Suspense>
            }
          />
        </Route>
      </Route>
    </Routes>
  )
}

export default App
